import { useEffect, useState } from "react";
import { fetchPage } from "@/lib/api";
import SEO from "@/components/SEO";
import Section from "@/components/Section";
import { Loader2 } from "lucide-react";

export default function Page({ slug }) {
  const [page, setPage] = useState(null);
  const [err, setErr] = useState(null);

  // Reset prerender-ready flag on every navigation so it doesn't leak
  // between routes during static prerendering.
  useEffect(() => {
    if (typeof window !== "undefined") window.__prerender_ready = false;
  }, [slug]);

  useEffect(() => {
    let alive = true;
    setPage(null);
    setErr(null);
    fetchPage(slug)
      .then((p) => alive && setPage(p))
      .catch((e) => alive && setErr(e?.message || "Failed to load page"));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Signal the prerenderer once page meta is in state. A small timeout lets
  // react-helmet-async flush <title> and <meta> into <head> before snapshot.
  useEffect(() => {
    if (page?.meta_description) {
      const t = setTimeout(() => {
        if (typeof window !== "undefined") window.__prerender_ready = true;
      }, 50);
      return () => clearTimeout(t);
    }
  }, [page?.meta_description]);

  if (err) {
    return (
      <div className="aoa-container py-32 text-center">
        <p className="aoa-muted">{err}</p>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="aoa-container py-32 flex items-center justify-center text-slate-400" data-testid="page-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={page.id === "home" ? undefined : page.title}
        description={page.meta_description}
        ogImage={page.og_image}
        path={page.route}
      />
      <div data-testid={`page-${page.id}`}>
        {(page.sections || []).map((s, i) => (
          <Section key={s.key} section={s} pageTitle={page.title} isFirst={i === 0} />
        ))}
      </div>
    </>
  );
}
